import React, { useRef, useState } from 'react';
import { IconCheck, IconMessage2, IconServer2, IconTag, IconX } from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import type { Group } from '../../../entities/group';
import { fetchProviderOptions } from '../../../entities/provider';
import { fetchSmsTemplateOptions } from '../../../entities/sms-template';
import type { User } from '../../../entities/user';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { logAction } from '../../../shared/lib/userLogger';
import { Select, type SelectOption } from '../../../shared/ui/Select';
import { Button } from '../../../shared/ui/Button';
import { Input } from '../../../shared/ui/Input';
import { UserSearchInput } from '../../../shared/ui/UserSearchInput/UserSearchInput';
import { type GroupForm, EMPTY_GROUP_FORM } from '../model/types';

const buildInitialOptions = (allUsers: User[], ids: string[]): SelectOption[] => {
  if (!ids.length) return [];
  return allUsers
    .filter(u => ids.includes(u.id))
    .map(u => ({ value: u.id, label: u.name || u.email }));
}

interface GroupModalProps {
  mode: 'add' | 'edit';
  initial?: Group | null;
  allUsers: User[];
  onClose: () => void;
  onSaved: () => void;
}

const GroupModal: React.FC<GroupModalProps> = ({ mode, initial, allUsers, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm<GroupForm>({
    defaultValues:
      mode === 'edit' && initial
        ? {
            name: initial.name ?? '',
            managers: initial.managers ?? [],
            usersIds: initial.usersIds ?? [],
            providerId: initial.providerId ?? '',
            providerName: initial.expand?.providerId?.name ?? '',
            templateSmsId: initial.templateSmsId ?? '',
            templateSmsName: initial.expand?.templateSmsId?.name ?? '',
          }
        : EMPTY_GROUP_FORM,
  });
  const providerName = watch('providerName');
  const templateSmsName = watch('templateSmsName');
  const managers = watch('managers');
  const usersIds = watch('usersIds');
  const managerInitialOptions = buildInitialOptions(allUsers, managers);
  const usersInitialOptions = buildInitialOptions(allUsers, usersIds);

  const handleFormSubmit = handleSubmit(async values => {
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: values.name,
        managers: values.managers,
        usersIds: values.usersIds,
        providerId: values.providerId,
        templateSmsId: values.templateSmsId,
      };
      if (mode === 'add') {
        await pb.collection('groups').create(payload);
        logAction(`Created group: ${values.name}`, 'ADD');
      } else {
        await pb.collection('groups').update(initial!.id, payload);
        logAction(`Edited group: ${values.name}`, 'EDIT');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getPbErrorMessage(err));
    } finally {
      setSaving(false);
    }
  });

  return (
    <div className="mo open" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="cm cm-create" style={{ maxWidth: 520, overflow: 'visible' }}>
        <div className="cm-top">
          <div className="cm-ti">{mode === 'add' ? 'Создать группу' : 'Редактировать группу'}</div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={16} stroke={2} />
          </button>
        </div>
        <form className="cm-create-body" onSubmit={handleFormSubmit} style={{ gap: 16, overflow: 'visible' }}>
          <div className="adm-field">
            <label className="adm-label">
              Название <span className="adm-req">*</span>
            </label>
            <Controller
              control={control}
              name="name"
              rules={{ required: 'Укажите название группы' }}
              render={({ field }) => (
                <div className="adm-input-wrap">
                  <IconTag size={15} stroke={2} className="adm-input-icon" />
                  <Input
                    {...field}
                    className="adm-input"
                    placeholder="Введите название группы..."
                  />
                </div>
              )}
            />
            {errors.name?.message ? <div className="form-field-error">{errors.name.message}</div> : null}
          </div>
          <div className="adm-field">
            <label className="adm-label">
              Тимлиды <span className="adm-req">*</span>
              <span className="adm-hint"> — до 10</span>
            </label>
            <Controller
              control={control}
              name="managers"
              rules={{ validate: value => value.length > 0 || 'Укажите хотя бы одного тимлида' }}
              render={({ field }) => (
                <UserSearchInput
                  mode="multi"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Выберите тимлидов..."
                  initialOptions={managerInitialOptions}
                  includeBlocked
                />
              )}
            />
            {errors.managers?.message ? <div className="form-field-error">{errors.managers.message}</div> : null}
          </div>
          <div className="adm-field">
            <label className="adm-label">
              Менеджеры
              {usersIds.length > 0 && <span className="adm-hint"> — выбрано {usersIds.length}</span>}
            </label>
            <Controller
              control={control}
              name="usersIds"
              render={({ field }) => (
                <UserSearchInput
                  mode="multi"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Выберите менеджеров..."
                  initialOptions={usersInitialOptions}
                  includeBlocked
                />
              )}
            />
          </div>
          <div className="adm-field">
            <label className="adm-label">Провайдер</label>
            <Controller
              control={control}
              name="providerId"
              render={({ field }) => (
                <Select
                  fetchOptions={fetchProviderOptions}
                  options={field.value && providerName ? [{ value: field.value, label: providerName }] : []}
                  value={field.value ?? ''}
                  onChange={(val, opt) => {
                    field.onChange(val);
                    setValue('providerName', opt?.label ?? '');
                  }}
                  placeholder="Выберите провайдера"
                  icon={<IconServer2 size={15} stroke={2} />}
                  portal
                />
              )}
            />
          </div>
          <div className="adm-field">
            <label className="adm-label">Шаблон SMS</label>
            <Controller
              control={control}
              name="templateSmsId"
              render={({ field }) => (
                <Select
                  fetchOptions={fetchSmsTemplateOptions}
                  options={field.value && templateSmsName ? [{ value: field.value, label: templateSmsName }] : []}
                  value={field.value ?? ''}
                  onChange={(val, opt) => {
                    field.onChange(val);
                    setValue('templateSmsName', opt?.label ?? '');
                  }}
                  placeholder="Выберите шаблон"
                  icon={<IconMessage2 size={15} stroke={2} />}
                  portal
                />
              )}
            />
          </div>
          {error && <div className="adm-form-error">{error}</div>}
          <div className="adm-form-actions">
            <Button variant="adm-cancel" type="button" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="adm-save" type="submit" disabled={saving}>
              {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
              {mode === 'add' ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { GroupModal };
